import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import AppDeploymentsService from '../../../services/appDeploymentsService';
import * as cardTemplatesService from '../../../services/cardTemplatesService';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../config/firebase';
import { doc, getDoc, collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { updateDocument, getDocument } from '../../../services/dbService';

const DeployStatusUpdateLogic = () => {
  const { fileId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Add a ref to track the last refresh time
  const lastRefreshTime = useRef(0);
  
  const [loading, setLoading] = useState(true);
  const [deploymentStatus, setDeploymentStatus] = useState(null);
  const [cardTemplates, setCardTemplates] = useState([]);
  const [selectedCardId, setSelectedCardId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('in-progress');
  const [appName, setAppName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [originalStoreUrl, setOriginalStoreUrl] = useState(''); // Track original URL for comparison
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [userId, setUserId] = useState('');
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [userAppId, setUserAppId] = useState(''); // Store userApp document ID
  
  // Function to reload deployment data after update with debounce
  const reloadData = useCallback(async () => {
    // Debounce - prevent refreshing more than once every 1 second
    const now = Date.now();
    if (now - lastRefreshTime.current < 1000) {
      console.log('Refresh throttled');
      return;
    }
    
    lastRefreshTime.current = now;
    
    try {
      setLoading(true);
      setError('');
      
      const appDeploymentsService = new AppDeploymentsService();
      
      // First get userApp data to ensure we have latest store URL
      if (userAppId) {
        try {
          const userAppData = await getDocument('userApps', userAppId);
          if (userAppData && userAppData.storeUrl) {
            setStoreUrl(userAppData.storeUrl);
            setOriginalStoreUrl(userAppData.storeUrl);
          }
        } catch (error) {
          console.error('Error fetching userApp data:', error);
        }
      }
      
      // Load deployment status
      const deployment = await appDeploymentsService.getDeploymentByFileId(fileId);
      if (deployment) {
        setDeploymentStatus(deployment);
        
        // Update selected card and status
        if (deployment.currentStage) {
          setSelectedCardId(deployment.currentStage);
          
          // Set current status for the selected stage
          if (deployment.stages && deployment.stages[deployment.currentStage]) {
            const currentStageStatus = deployment.stages[deployment.currentStage].status;
            if (currentStageStatus) {
              setSelectedStatus(standardizeStatus(currentStageStatus));
            }
          }
        }
        
        // Update store URL if available and we don't have one from userApp
        if (deployment.storeUrl && !originalStoreUrl) {
          setStoreUrl(deployment.storeUrl);
          setOriginalStoreUrl(deployment.storeUrl);
        }
        
        // Show success message
        setSuccess(true);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.error('Error reloading deployment data:', err);
      setError('Failed to reload deployment data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fileId, userAppId]);
  
  // Standardize status values for consistent UI display
  const standardizeStatus = (rawStatus) => {
    if (!rawStatus) return 'pending';
    
    // Convert all status values to the three standard options
    if (rawStatus === 'processing' || rawStatus === 'in_progress' || rawStatus === 'started' || rawStatus === 'analyzing') {
      return 'in-progress';
    } else if (rawStatus === 'done' || rawStatus === 'finished' || rawStatus === 'completed') {
      return 'completed';
    } else if (rawStatus === 'not_started' || rawStatus === 'waiting' || rawStatus === 'pending' || rawStatus === 'initialized') {
      return 'pending';
    }
    
    // Default to the original value if no match
    return rawStatus;
  };
  
  // Load deployment status and card templates
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Create service instances
        const appDeploymentsService = new AppDeploymentsService();
        
        // Get app data first to have the fileName
        const appData = await appDeploymentsService.getAppDataByFileId(fileId);
        if (appData) {
          // Set app name from fileName
          if (appData.fileName) {
            setAppName(appData.fileName);
            console.log("Setting appName from fileName:", appData.fileName);
          }
          
          // Set user info if available
          if (appData.userId) {
            setUserId(appData.userId);
            
            if (appData.userName) {
              setUserName(appData.userName);
            }
            
            if (appData.userEmail) {
              setUserEmail(appData.userEmail);
            }
          }
          
          // Store userApp document ID
          if (appData.id) {
            setUserAppId(appData.id);
          }
          
          // Store original storeUrl if available
          if (appData.storeUrl) {
            setStoreUrl(appData.storeUrl);
            setOriginalStoreUrl(appData.storeUrl);
          }
        }
        
        // Load deployment status
        const deployment = await appDeploymentsService.getDeploymentByFileId(fileId);
        if (!deployment) {
          // If no deployment exists, we'll create one
          setDeploymentStatus({ fileId });
        } else {
          setDeploymentStatus(deployment);
          
          // Set current card as default
          if (deployment.currentStage) {
            setSelectedCardId(deployment.currentStage);
            
            // Set current status for the selected stage
            if (deployment.stages && deployment.stages[deployment.currentStage]) {
              const currentStageStatus = deployment.stages[deployment.currentStage].status;
              if (currentStageStatus) {
                setSelectedStatus(currentStageStatus);
              }
            }
          }
          
          // Set app name and URL if available
          if (deployment.appName && !appName) {
            setAppName(deployment.appName);
          }
          if (deployment.storeUrl) {
            setStoreUrl(deployment.storeUrl);
            // Only update original if not already set
            if (!originalStoreUrl) {
              setOriginalStoreUrl(deployment.storeUrl);
            }
          }
          
          // Set user information if available
          if (deployment.userId && !userId) {
            setUserId(deployment.userId);
            
            if (deployment.userName && !userName) {
              setUserName(deployment.userName);
            }
            
            if (deployment.userEmail && !userEmail) {
              setUserEmail(deployment.userEmail);
            }
          }
        }
        
        // Load card templates
        const templates = await cardTemplatesService.getAllCardTemplates();
        // Filter only stage type cards
        const stageTemplates = templates.filter(template => template.type === 'stage');
        setCardTemplates(stageTemplates);
        
        // Set default selected card if not already set
        if (stageTemplates.length > 0 && !selectedCardId) {
          // Get currentStage from URL params directly
          const currentStageFromUrl = searchParams.get('currentStage');
          
          if (currentStageFromUrl && stageTemplates.find(t => t.id === currentStageFromUrl)) {
            // If a valid currentStage is provided in the URL, use it
            setSelectedCardId(currentStageFromUrl);
            
            // Set the status for this stage
            if (deployment?.stages?.[currentStageFromUrl]?.status) {
              setSelectedStatus(standardizeStatus(deployment.stages[currentStageFromUrl].status));
            }
          } else {
            // Fallback to first card if no valid currentStage specified
            setSelectedCardId(stageTemplates[0].id);
          }
        }
        
      } catch (err) {
        console.error('Error loading deployment data:', err);
        setError('Failed to load deployment data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [fileId, searchParams]);
  
  // Ensure selectedStatus is always in sync with the selected card
  useEffect(() => {
    if (selectedCardId && deploymentStatus?.stages?.[selectedCardId]) {
      const currentStatus = deploymentStatus.stages[selectedCardId].status || 'pending';
      
      // Standardize the status for UI display
      const normalizedStatus = standardizeStatus(currentStatus);
      
      setSelectedStatus(normalizedStatus);
    } else {
      // Default to pending if no data is found
      setSelectedStatus('pending');
    }
  }, [selectedCardId, deploymentStatus]);
  
  // Force immediate selection on mount
  useEffect(() => {
    // Force a default status if nothing is selected
    setTimeout(() => {
      if (deploymentStatus?.stages?.[selectedCardId]?.status) {
        let status = deploymentStatus.stages[selectedCardId].status;
        
        // Normalize status
        if (status === 'processing') {
          status = 'in-progress';
        }
        
        setSelectedStatus(status);
      }
    }, 500); // Small delay to ensure DOM is ready
  }, []); // Run once on mount
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCardId) {
      setError('Please select a card.');
      return;
    }
    
    try {
      setSaving(true);
      setLoading(true); // Show loading state for the entire form
      setError('');
      
      const appDeploymentsService = new AppDeploymentsService();
      const now = new Date().toISOString();
      
      // Check if deployment exists
      if (!deploymentStatus.id) {
        // Create a new deployment
        await appDeploymentsService.createDeployment({
          fileId,
          status: 'in-progress',
          currentStage: selectedCardId,
          startedAt: now,
          updatedAt: now,
          lastInteractionAt: now,
          storeUrl: storeUrl
        });
      } else {
        // Update existing deployment
        const updateData = {
          currentStage: selectedCardId,
          status: selectedStatus,
          updatedAt: now,
          lastInteractionAt: now,
          storeUrl: storeUrl
        };
        
        // Update stage status
        if (deploymentStatus.stages && deploymentStatus.stages[selectedCardId]) {
          updateData.stages = {
            [selectedCardId]: {
              status: selectedStatus,
            }
          };
          
          // If stage is changing to 'in-progress' and has no startedAt, set it
          if (selectedStatus === 'in-progress' && !deploymentStatus.stages[selectedCardId].startedAt) {
            updateData.stages[selectedCardId].startedAt = now;
          }
          
          // If stage is changing to 'completed' and has no completedAt, set it
          if (selectedStatus === 'completed') {
            updateData.stages[selectedCardId].completedAt = now;
            
            // If the status is completed and send email is checked, send notification regardless of card position
            if (sendEmail && userId && fileId) {
              try {
                // Call the function with user email and name if available
                const sendAppStatusUpdateEmail = httpsCallable(functions, 'sendAppStatusUpdateEmail');
                await sendAppStatusUpdateEmail({
                  userId: userId,
                  appId: fileId,
                  sendEmail: true,
                  userEmail: userEmail,
                  userName: userName,
                  appName: appName || "Your application",
                  currentStage: selectedCardId
                });
              } catch (emailError) {
                console.error('Error sending status update email:', emailError);
                // Don't stop the deployment update for email errors
              }
            }
            
            // Find the next card in sequence
            const allStageIds = cardTemplates.map(t => t.id);
            
            const currentCardIndex = cardTemplates.findIndex(template => template.id === selectedCardId);
            
            const nextCardIndex = currentCardIndex + 1;
            
            // If there is a next card, set it to 'in-progress'
            if (nextCardIndex < cardTemplates.length) {
              const nextCardId = cardTemplates[nextCardIndex].id;
              
              // Update the current stage to the next card
              updateData.currentStage = nextCardId;
              
              // Initialize or update the next stage
              if (!updateData.stages) {
                updateData.stages = {};
              }
              
              updateData.stages[nextCardId] = {
                status: 'in-progress',
                startedAt: now
              };
            }
          }
        }
        
        await appDeploymentsService.updateDeployment(deploymentStatus.id, updateData);
        
        // Update storeUrl in userApp document if it has changed
        if (storeUrl && storeUrl !== originalStoreUrl && userAppId) {
          try {
            // Update the storeUrl field in the userApp document directly with the ID we already have
            await updateDocument('userApps', userAppId, {
              storeUrl: storeUrl,
              lastUpdated: now
            });
            
            // Update original store URL after successful update
            setOriginalStoreUrl(storeUrl);
            
            console.log(`Updated storeUrl in userApp document ${userAppId}`);
          } catch (updateError) {
            console.error('Error updating storeUrl in userApp:', updateError);
            // Don't stop the process for this error
          }
        } else if (storeUrl && storeUrl !== originalStoreUrl && !userAppId) {
          // If we don't have the userAppId yet, find it by fileId
          try {
            // Find the userApp document by fileId
            const q = query(
              collection(db, 'userApps'),
              where('fileId', '==', fileId)
            );
            
            const querySnapshot = await getDocs(q);
            
            if (!querySnapshot.empty) {
              const userAppDoc = querySnapshot.docs[0];
              const docId = userAppDoc.id;
              
              // Store the ID for future use
              setUserAppId(docId);
              
              // Update the storeUrl field in the userApp document
              await updateDocument('userApps', docId, {
                storeUrl: storeUrl,
                lastUpdated: now
              });
              
              // Update original store URL after successful update
              setOriginalStoreUrl(storeUrl);
              
              console.log(`Updated storeUrl in userApp document ${docId}`);
            }
          } catch (updateError) {
            console.error('Error updating storeUrl in userApp:', updateError);
            // Don't stop the process for this error
          }
        }
      }
      
      setSuccess(true);
      
      // Reload data to reflect changes
      await reloadData();
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating deployment status:', err);
      setError('Failed to update deployment status. Please try again.');
    } finally {
      setSaving(false);
      setLoading(false); // Reset loading state
    }
  };
  
  // Get status label
  const getStatusLabel = (status) => {
    if (!status) return 'Unknown';
    
    switch (status) {
      case 'in-progress': return 'In Progress';
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      default: return status.charAt(0).toUpperCase() + status.slice(1);
    }
  };

  const handleStoreUrlChange = (e) => {
    setStoreUrl(e.target.value);
  };

  const handleCardSelect = (e) => {
    setSelectedCardId(e.target.value);
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
  };

  const handleSendEmailChange = (e) => {
    setSendEmail(e.target.checked);
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  return {
    fileId,
    loading,
    deploymentStatus,
    cardTemplates,
    selectedCardId,
    selectedStatus,
    appName,
    storeUrl,
    saving,
    error,
    success,
    sendEmail,
    userName,
    userEmail,
    getStatusLabel,
    handleSubmit,
    handleStoreUrlChange,
    handleCardSelect,
    handleStatusSelect,
    handleSendEmailChange,
    handleBackClick,
    reloadData
  };
};

export default DeployStatusUpdateLogic; 